import type { CouponData } from 'types/payment'
import type { VariationAttribute } from 'types/variation'

type BestCouponResult = {
  coupon: CouponData
  discountedPrice: number
}

/**
 * Coupon maths, kept free of React so both the app's useSuggestedCouponDiscount
 * hook and the SDK's server route price a coupon the same way.
 */
export function computeDiscountedPrice(
  currentPrice: number,
  couponCalc: { type: string; value: number; productHashes?: string[] } | null,
  productHash: string
): number | null {
  if (!couponCalc) return null

  const { type, value, productHashes } = couponCalc
  let discount = 0

  if (type === 'fixed_product') {
    if (productHashes && !productHashes.includes(productHash)) return null
    discount = Math.min(value * 100, currentPrice)
  } else if (type === 'fixed_cart') {
    discount = Math.min(value * 100, currentPrice)
  } else if (type === 'percent') {
    discount = (currentPrice * value) / 100
  }

  return Math.max(0, currentPrice - discount)
}

/**
 * True when /coupons/validate refused the coupon for this cart. The API's catch-all
 * rest_parameter_error and a network fault (a plain Error) are failures, not refusals.
 */
export const isCouponRefusal = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || error instanceof Error) return false
  return (error as { code?: unknown }).code === 'rest_coupons_validate_error'
}

/** A code that keeps discounting a subscription's renewals until its payment limit. */
export const isRecurringCoupon = (type?: string): boolean => type === 'recurring_percent' || type === 'recurring_fee'

/**
 * What a recurring code takes off one payment of a subscription line, in pence. Matches WooCommerce
 * Subscriptions: a percent of the line, or a fixed amount per unit that never exceeds the unit price.
 */
export function recurringDiscount(type: string, value: number, price: number, quantity = 1): number {
  if (type === 'recurring_percent') return Math.round((price * quantity * value) / 100)
  if (type === 'recurring_fee') return Math.min(value * 100, price) * quantity

  return 0
}

/** The later payments a renewal code still discounts after the first, at the subscription line's discounted price. */
export function discountedRepeats(
  coupon: { type?: string; payments?: number | null } | null,
  price: number,
  discount: number
): { total: number; count: number } | null {
  if (!isRecurringCoupon(coupon?.type) || !(coupon.payments > 1) || !price) return null

  return { total: Math.max(0, price - discount), count: coupon.payments - 1 }
}

/** The note under a subscription's discount: how many payments the code covers. */
export function subscriptionDiscountNote(coupon: { type?: string; payments?: number | null } | null): string {
  const payments = isRecurringCoupon(coupon?.type) ? (coupon?.payments ?? 0) : 0

  if (payments > 1) return `Please note: This discount applies to your first ${payments} payments.`

  return 'Please note: Discounts are only applied to the first order within a subscription.'
}

export const isVariationAttribute = (value: unknown): value is VariationAttribute => {
  return value && typeof value === 'object' && 'slug' in value && 'label' in value
}

/**
 * Given an array of validated coupons, picks the one that results in the lowest
 * price (i.e. the highest discount). Uses the existing computeDiscountedPrice
 * function so all coupon types (percent, fixed_cart, fixed_product) are handled
 * consistently.
 *
 * Returns null when no valid/useful coupon is found.
 */
export function findBestCoupon(coupons: CouponData[], currentPrice: number, productHash: string): BestCouponResult | null {
  let best: BestCouponResult | null = null

  for (const coupon of coupons) {
    if (!coupon.valid || !coupon.type || coupon.value === undefined) continue

    const discountedPrice = computeDiscountedPrice(
      currentPrice,
      {
        type: coupon.type,
        value: coupon.value,
        productHashes: coupon.product_hashes
      },
      productHash
    )

    if (discountedPrice === null || discountedPrice >= currentPrice) {
      continue
    }

    if (!best || discountedPrice < best.discountedPrice) {
      best = {
        coupon,
        discountedPrice
      }
    }
  }

  return best
}

/**
 * Saving from a percent coupon. A line the coupon validation checked takes the API's amount;
 * a line it did not check (added since) takes the rate when the local rule covers it.
 * Partial when some line gets less than the full rate.
 */
export function percentCouponSaving<T extends { id: string; price: number }>(
  lines: T[],
  coupon: Pick<CouponData, 'item_discounts'>,
  rate: number,
  isCoveredLocally: (line: T) => boolean
): { saving: number; partial: boolean } {
  const checked = coupon.item_discounts ?? {}
  // The API sums the lines that share an id (one product for each patient), so take each id once.
  const checkedLines = new Map<string, { price: number; count: number }>()
  let saving = 0
  let partial = false

  for (const line of lines) {
    if (Object.hasOwn(checked, line.id)) {
      const group = checkedLines.get(line.id) ?? { price: 0, count: 0 }
      checkedLines.set(line.id, { price: group.price + line.price, count: group.count + 1 })
    } else if (isCoveredLocally(line)) {
      saving += (line.price * rate) / 100
    } else if (line.price > 0) {
      partial = true
    }
  }

  for (const [id, group] of checkedLines) {
    saving += checked[id]
    // WooCommerce rounds each line down, so allow a penny a line.
    if (checked[id] < (group.price * rate) / 100 - group.count) partial = true
  }

  return { saving, partial }
}
